import React, { createContext, useState, useContext, useEffect, useCallback } from 'react';
import axios from 'axios';
import { log } from '../lib/logger';
import { clearActiveDraftOrderId } from '../lib/activeDraftOrder';

const AuthContext = createContext();

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Configure axios to send cookies with every request (for httpOnly cookies)
axios.defaults.withCredentials = true;
// Global network timeout — prevents the UI from getting stuck on
// indefinite "Loading..." / "Submitting..." states when a proxy silently
// drops a request. 60s gives slow Gemini-backed endpoints plenty of
// headroom while still bounding the worst case the user can see.
axios.defaults.timeout = 60000;

// ---------------------------------------------------------------------------
// CSRF — double-submit cookie pattern.
//
// Backend issues a non-httpOnly `csrf_token` cookie alongside the auth
// cookie on /login + /me. We read it on every state-changing request and
// echo it back as `X-CSRF-Token` so the backend's CSRFMiddleware can
// confirm the request originated from a real same-origin context (the
// cookie is unreadable cross-origin, so a malicious site can't forge
// the matching header even though the browser auto-attaches the cookie).
//
// Module-level interceptor (NOT inside AuthProvider) so it covers every
// axios call in the app — including ones made before the provider mounts
// or from non-React callsites (`utils/api`, etc.).
// ---------------------------------------------------------------------------
const UNSAFE_METHODS = new Set(['post', 'put', 'patch', 'delete']);
function readCookie(name) {
  if (typeof document === 'undefined') return null;
  const match = document.cookie.match(new RegExp('(?:^|; )' + name.replace(/[$()*+./?[\\\]^{|}]/g, '\\$&') + '=([^;]*)'));
  return match ? decodeURIComponent(match[1]) : null;
}
axios.interceptors.request.use((config) => {
  const method = (config.method || 'get').toLowerCase();
  if (UNSAFE_METHODS.has(method)) {
    const token = readCookie('csrf_token');
    if (token) {
      config.headers = config.headers || {};
      // Don't clobber an explicit override (e.g. tests, retries).
      if (!config.headers['X-CSRF-Token'] && !config.headers['x-csrf-token']) {
        config.headers['X-CSRF-Token'] = token;
      }
    }
  }
  return config;
});

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const initAuth = useCallback(async () => {
    try {
      // Try to get current user using httpOnly cookie
      // Cookie is automatically sent with request (no need to manually include it)
      const response = await axios.get(`${API_URL}/api/auth/me`);
      setUser(response.data);
      // Refresh the "last login" greeting stamp so the badge shows
      // the most recent session even when the user came in via 2FA
      // verify (which doesn't pass through the /login handler).
      try {
        const u = response.data || {};
        if (u.email) {
          localStorage.setItem('eg3_last_login', JSON.stringify({
            email: u.email,
            first_name: u.first_name || '',
            last_name: u.last_name || '',
            ts: Date.now(),
          }));
        }
      } catch {
        /* ignore */
      }
    } catch (error) {
      // Not authenticated or cookie expired
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    // Emergent Google OAuth return leg: AuthCallback exchanges the
    // session_id and establishes the cookie FIRST — skip the early /me
    // probe so it doesn't race and 401.
    if (window.location.hash?.includes('session_id=')) {
      setLoading(false);
      return;
    }
    initAuth();
  }, [initAuth]); // Include initAuth in dependencies

  // Global 401 safety net.
  //
  // Any authenticated endpoint can suddenly return 401 if the httpOnly
  // session cookie expires server-side mid-session. Without this, the
  // React tree keeps rendering as if the user is logged in (because
  // `user` is still in state from the last /me call) and the next
  // request silently fails with a cryptic "Not authenticated" detail.
  //
  // The interceptor re-runs /me on any 401 it sees, which clears `user`
  // and lets the UI re-render the appropriate signed-out / guest-prompt
  // affordance. Skips /auth/* itself to avoid recursion when /me is
  // the call that returned 401.
  useEffect(() => {
    const id = axios.interceptors.response.use(
      (resp) => resp,
      (error) => {
        const status = error?.response?.status;
        const url = error?.config?.url || '';
        if (status === 401 && !url.includes('/api/auth/')) {
          // Re-sync auth state on the next tick so we don't recurse
          // through the interceptor itself.
          setTimeout(() => { initAuth(); }, 0);
        }
        return Promise.reject(error);
      },
    );
    return () => axios.interceptors.response.eject(id);
  }, [initAuth]);

  const login = async (email, password, rememberMe = false) => {
    try {
      const response = await axios.post(`${API_URL}/api/auth/login`, {
        email,
        password,
        remember_me: rememberMe
      });

      // 2FA gate (iter102) — admin accounts get a challenge token
      // instead of an auth cookie. iter111: the JWT is now stashed in
      // an httpOnly cookie (`eg3_2fa_challenge`) by the backend, so we
      // no longer write it to sessionStorage. We pass the email +
      // remember_me through React Router's `location.state` (in-memory
      // only) so the TOTP pages can render the account label without
      // a round-trip to the backend, while the JWT itself stays
      // unreadable to JavaScript (XSS-resistant).
      if (response.data?.requires_2fa) {
        return {
          success: true,
          requires2fa: true,
          enrollmentRequired: !!response.data.enrollment_required,
          email: response.data.email || email,
          rememberMe,
        };
      }

      // Backend sets httpOnly cookie automatically
      // Just update frontend state with user info
      setUser(response.data.user);

      // Persist a non-sensitive "last login" stamp so the /login page
      // can greet returning operators on next visit (cookie expired but
      // localStorage retained). NEVER store tokens / passwords here —
      // just the human-friendly fields needed for the greeting.
      try {
        const u = response.data.user || {};
        localStorage.setItem('eg3_last_login', JSON.stringify({
          email: u.email || email,
          first_name: u.first_name || '',
          last_name: u.last_name || '',
          ts: Date.now(),
        }));
      } catch {
        // localStorage can throw in private mode — non-fatal.
      }

      return { success: true, user: response.data.user };
    } catch (error) {
      return {
        success: false,
        error: error.response?.data?.detail || 'Login failed'
      };
    }
  };

  const register = async (userData) => {
    try {
      await axios.post(`${API_URL}/api/auth/register`, userData);
      // Auto-login after registration
      return await login(userData.email, userData.password);
    } catch (error) {
      return {
        success: false,
        error: error.response?.data?.detail || 'Registration failed'
      };
    }
  };

  // Guest checkout — create an anonymous account from just an email so the
  // visitor can place an order without signing up first. Backend stamps
  // is_guest=true on the user so OrderConfirmationPage knows to show the
  // claim-account card after submission.
  const guestSignup = async ({ email, first_name, last_name }) => {
    try {
      const resp = await axios.post(`${API_URL}/api/auth/guest-signup`, {
        email,
        first_name: first_name || '',
        last_name: last_name || '',
      });
      setUser(resp.data.user);
      return { success: true, user: resp.data.user };
    } catch (error) {
      return {
        success: false,
        status: error.response?.status,
        error: error.response?.data?.detail || 'Could not continue as guest',
      };
    }
  };

  // Upgrade the currently-authenticated guest user into a full account by
  // setting a password (and optionally correcting name). Refreshes the
  // stored user so `isGuest` flips to false everywhere the context is read.
  const claimAccount = async ({ password, first_name, last_name }) => {
    try {
      const resp = await axios.post(`${API_URL}/api/auth/claim-account`, {
        password,
        first_name: first_name || '',
        last_name: last_name || '',
      });
      setUser(resp.data.user);
      return { success: true, user: resp.data.user };
    } catch (error) {
      return {
        success: false,
        error: error.response?.data?.detail || 'Could not claim account',
      };
    }
  };

  const logout = async () => {
    try {
      // Call backend to clear httpOnly cookie
      await axios.post(`${API_URL}/api/auth/logout`);
    } catch (error) {
      // Logout locally even if backend call fails
      log.error('Logout API call failed:', error);
    } finally {
      // Clear frontend state
      setUser(null);
      // Drop any in-progress draft cart pointer so the next user on
      // this tab doesn't accidentally pick up the previous user's draft.
      clearActiveDraftOrderId();
    }
  };

  const updateProfile = async (userData) => {
    try {
      // Cookie automatically sent, no need for Authorization header
      const response = await axios.put(`${API_URL}/api/users/me`, userData);
      setUser(response.data);
      return { success: true };
    } catch (error) {
      return {
        success: false,
        error: error.response?.data?.detail || 'Update failed'
      };
    }
  };

  const changePassword = async (currentPassword, newPassword) => {
    try {
      // Cookie automatically sent, no need for Authorization header
      await axios.post(`${API_URL}/api/auth/change-password`, {
        current_password: currentPassword,
        new_password: newPassword
      });
      return { success: true };
    } catch (error) {
      return {
        success: false,
        error: error.response?.data?.detail || 'Password change failed'
      };
    }
  };

  const value = {
    user,
    loading,
    // Alias — several consumers destructure `isLoading`; keep both to
    // prevent the undefined-guard bug that bounced admin deep-links.
    isLoading: loading,
    login,
    register,
    guestSignup,
    claimAccount,
    logout,
    updateProfile,
    changePassword,
    refreshAuth: initAuth,
    refreshUser: initAuth, // alias used by TOTP pages
    isAuthenticated: !!user,
    isAdmin: user?.is_admin || false,
    isGuest: !!user?.is_guest,
    pricingTier: user?.pricing_tier || 'stickeryou',
    isEvergreenTier: user?.pricing_tier === 'evergreen'
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
